import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import axios from 'axios';
import AuthContext from '../../contexts/AuthContext';

const DEFAULT_BOARD_KEY = 'dailyPlanQuickTaskBoard';

// Pick the default board for quick-tasks: saved preference, else a "Task List" board
// (preferring a Widget Financial one), else the first accessible board.
const resolveDefaultBoardId = (boards) => {
  const saved = localStorage.getItem(DEFAULT_BOARD_KEY);
  if (saved && boards.some((b) => b._id === saved)) return saved;
  const taskLists = boards.filter((b) => /task\s*list/i.test(b.name || ''));
  const wf = taskLists.find((b) => /widget/i.test(b.name || ''));
  return (wf || taskLists[0] || boards[0])?._id || '';
};

// Searchable picker over any card on any board the user can access (GET /api/search),
// plus the ability to create a brand-new task right from the picker.
const CardPicker = ({ onPick, onClose }) => {
  const { token } = useContext(AuthContext);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [boards, setBoards] = useState([]);
  const [createBoardId, setCreateBoardId] = useState('');
  const [creating, setCreating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Load the boards the user can post quick-tasks to, and seed the default selection.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/api/boards', { headers: { 'x-auth-token': token } });
        const list = res.data || [];
        setBoards(list);
        setCreateBoardId(resolveDefaultBoardId(list));
      } catch (e) {
        setBoards([]);
      }
    })();
  }, [token]);

  // Load the full open-task list up front; the search box filters it (debounced).
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const url = q.trim()
          ? `/api/dayplan/tasks?q=${encodeURIComponent(q.trim())}`
          : '/api/dayplan/tasks';
        const res = await axios.get(url, { headers: { 'x-auth-token': token } });
        setResults(res.data || []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, q.trim() ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, token]);

  const priorityColor = {
    Urgent: 'bg-red-500', High: 'bg-orange-400', Medium: 'bg-blue-400', Low: 'bg-gray-300'
  };

  const trimmed = q.trim();

  const createTask = async () => {
    if (!trimmed || !createBoardId || creating) return;
    setCreating(true);
    try {
      const res = await axios.post(
        '/api/dayplan/quick-task',
        { title: trimmed, boardId: createBoardId },
        { headers: { 'x-auth-token': token } }
      );
      onPick(res.data); // drop the new task straight into the slot
    } catch (e) {
      // Surface a lightweight failure without leaving the picker
      // eslint-disable-next-line no-alert
      alert(e.response?.data?.msg || 'Could not create the task. Try again.');
    } finally {
      setCreating(false);
    }
  };

  const saveDefaultBoard = (id) => {
    if (id) localStorage.setItem(DEFAULT_BOARD_KEY, id);
    setCreateBoardId(id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 dark:border-gray-800">
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && trimmed && results.length === 0) createTask(); }}
            placeholder="Filter your open tasks, or type a new task..."
            className="w-full px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-primary-blue/40"
          />
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading && <div className="px-4 py-6 text-center text-xs text-gray-400">Loading…</div>}
          {!loading && results.length === 0 && (
            <div className="px-4 py-6 text-center text-xs text-gray-400">
              {trimmed ? 'No matching tasks' : 'No open tasks assigned to you'}
            </div>
          )}
          {results.map((card) => (
            <button
              key={card._id}
              onClick={() => onPick(card)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 border-b border-gray-50 dark:border-gray-800 flex items-center gap-3 transition-colors">
              <span className={`h-2 w-2 rounded-full flex-shrink-0 ${priorityColor[card.priority] || 'bg-gray-300'}`} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{card.title}</span>
                <span className="block text-[11px] text-gray-400 truncate">{card.board?.name} · {card.column}</span>
              </span>
            </button>
          ))}
        </div>

        {/* Create-a-new-task row: shows whenever the user has typed something. */}
        {trimmed && (
          <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/40 flex items-center gap-2">
            <button
              onClick={createTask}
              disabled={creating || !createBoardId}
              className="flex-1 min-w-0 text-left text-sm font-semibold text-primary-blue hover:underline disabled:opacity-50 disabled:no-underline truncate">
              {creating ? 'Creating…' : <>+ Create “{trimmed}”</>}
            </button>
            <select
              value={createBoardId}
              onChange={(e) => setCreateBoardId(e.target.value)}
              className="shrink-0 max-w-[45%] text-[11px] px-2 py-1.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none">
              {boards.map((b) => (
                <option key={b._id} value={b._id}>{b.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Settings: choose the default board for new quick-tasks (saved per-browser). */}
        {showSettings && (
          <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60">
            <label className="block text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">
              Default board for new tasks
            </label>
            <select
              value={createBoardId}
              onChange={(e) => saveDefaultBoard(e.target.value)}
              className="w-full text-sm px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none">
              {boards.map((b) => (
                <option key={b._id} value={b._id}>{b.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between">
          <button
            onClick={() => setShowSettings((s) => !s)}
            className="text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-600">
            {showSettings ? 'Done' : '⚙ Default board'}
          </button>
          <button onClick={onClose} className="text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-600">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default CardPicker;
